// /src/reports/CardTransactionsReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";

// OneStop week start date
const ONE_STOP_WEEK1_START = new Date("2025-02-23"); // Sunday

function getOneStopWeekInfo(date) {
  const diffDays = Math.floor(
    (date - ONE_STOP_WEEK1_START) / (1000 * 60 * 60 * 24)
  );
  const weekNum = Math.floor(diffDays / 7) + 1;
  const weekStart = addDays(ONE_STOP_WEEK1_START, (weekNum - 1) * 7);
  const weekEnd = addDays(weekStart, 6);
  return { weekNum, weekStart, weekEnd };
}

export default function CardTransactionsReport({ fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("daily");

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, viewType]);

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("zreports")
      .select("*")
      .gte("date", fromDate)
      .lte("date", toDate);

    if (error) {
      toast.error("Error fetching card transactions data");
      setLoading(false);
      return;
    }

    const processed = data.map((z) => {
      const dateStr = format(new Date(z.date), "yyyy-MM-dd");

      const oneStopCard = parseFloat(z.one_stop_card) || 0;

      const snappyCard = z.online_partners?.filter(p => p.name === "Snappy").reduce((sum, p) => sum + (p.card || 0), 0) || 0;
      const deliverooCard = z.online_partners?.filter(p => p.name === "Deliveroo").reduce((sum, p) => sum + (p.card || 0), 0) || 0;
      const justEatCard = z.online_partners?.filter(p => p.name === "Just Eat").reduce((sum, p) => sum + (p.card || 0), 0) || 0;
      const otherCard = z.online_partners?.filter(p => !["Snappy", "Deliveroo", "Just Eat"].includes(p.name))
        .reduce((sum, p) => sum + (p.card || 0), 0) || 0;

      const onlineCardTotal = snappyCard + deliverooCard + justEatCard + otherCard;
      const totalCard = oneStopCard + onlineCardTotal;

      return {
        date: dateStr,
        oneStopCard,
        snappyCard,
        deliverooCard,
        justEatCard,
        otherCard,
        onlineCardTotal,
        totalCard
      };
    });

    setRows(processed);
    setLoading(false);
  }

  function groupByWeek(data) {
    const grouped = {};
    data.forEach((row) => {
      const { weekNum, weekStart, weekEnd } = getOneStopWeekInfo(new Date(row.date));
      const key = `Week ${weekNum} (${format(weekStart, "dd MMM")} - ${format(weekEnd, "dd MMM")})`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }

  function exportCSV() {
    const headers = [
      "Date/Week",
      "OneStop Card",
      "Snappy Card",
      "Deliveroo Card",
      "Just Eat Card",
      "Other Card",
      "Online Card Total",
      "Total Card Transactions"
    ];
    let csvRows = [headers.join(",")];

    if (viewType === "daily") {
      rows.forEach(r => {
        csvRows.push([
          r.date,
          r.oneStopCard.toFixed(2),
          r.snappyCard.toFixed(2),
          r.deliverooCard.toFixed(2),
          r.justEatCard.toFixed(2),
          r.otherCard.toFixed(2),
          r.onlineCardTotal.toFixed(2),
          r.totalCard.toFixed(2)
        ].join(","));
      });
    } else {
      const weeklyGroups = groupByWeek(rows);
      Object.keys(weeklyGroups).forEach(week => {
        weeklyGroups[week].forEach(r => {
          csvRows.push([
            week,
            r.oneStopCard.toFixed(2),
            r.snappyCard.toFixed(2),
            r.deliverooCard.toFixed(2),
            r.justEatCard.toFixed(2),
            r.otherCard.toFixed(2),
            r.onlineCardTotal.toFixed(2),
            r.totalCard.toFixed(2)
          ].join(","));
        });
      });
    }

    saveAs(new Blob([csvRows.join("\n")], { type: "text/csv" }), `card_transactions_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  }

  if (loading) return <p>Loading Card Transactions Report...</p>;

  if (viewType === "daily") {
    const totals = rows.reduce((acc, r) => {
      acc.oneStopCard += r.oneStopCard;
      acc.snappyCard += r.snappyCard;
      acc.deliverooCard += r.deliverooCard;
      acc.justEatCard += r.justEatCard;
      acc.otherCard += r.otherCard;
      acc.onlineCardTotal += r.onlineCardTotal;
      acc.totalCard += r.totalCard;
      return acc;
    }, {
      oneStopCard:0, snappyCard:0, deliverooCard:0, justEatCard:0, otherCard:0, onlineCardTotal:0, totalCard:0
    });

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={e => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>Export CSV</button>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>OneStop Card</th>
              <th>Snappy Card</th>
              <th>Deliveroo Card</th>
              <th>Just Eat Card</th>
              <th>Other Card</th>
              <th>Online Card Total</th>
              <th>Total Card Transactions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.oneStopCard.toFixed(2)}</td>
                <td>{r.snappyCard.toFixed(2)}</td>
                <td>{r.deliverooCard.toFixed(2)}</td>
                <td>{r.justEatCard.toFixed(2)}</td>
                <td>{r.otherCard.toFixed(2)}</td>
                <td>{r.onlineCardTotal.toFixed(2)}</td>
                <td style={{ fontWeight:"bold" }}>{r.totalCard.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight:"bold", background:"#f0f0f0" }}>
              <td>Total</td>
              <td>{totals.oneStopCard.toFixed(2)}</td>
              <td>{totals.snappyCard.toFixed(2)}</td>
              <td>{totals.deliverooCard.toFixed(2)}</td>
              <td>{totals.justEatCard.toFixed(2)}</td>
              <td>{totals.otherCard.toFixed(2)}</td>
              <td>{totals.onlineCardTotal.toFixed(2)}</td>
              <td>{totals.totalCard.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    const weeklyGroups = groupByWeek(rows);
    const grandTotals = rows.reduce((acc, r) => {
      acc.oneStopCard += r.oneStopCard;
      acc.snappyCard += r.snappyCard;
      acc.deliverooCard += r.deliverooCard;
      acc.justEatCard += r.justEatCard;
      acc.otherCard += r.otherCard;
      acc.onlineCardTotal += r.onlineCardTotal;
      acc.totalCard += r.totalCard;
      return acc;
    }, {
      oneStopCard:0, snappyCard:0, deliverooCard:0, justEatCard:0, otherCard:0, onlineCardTotal:0, totalCard:0
    });

    return (
      <div>
        <label>View Type: </label>
        <select value={viewType} onChange={e => setViewType(e.target.value)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>Export CSV</button>

        {Object.keys(weeklyGroups).map(week => {
          const weekRows = weeklyGroups[week];
          const weekTotals = weekRows.reduce((acc, r) => {
            acc.oneStopCard += r.oneStopCard;
            acc.snappyCard += r.snappyCard;
            acc.deliverooCard += r.deliverooCard;
            acc.justEatCard += r.justEatCard;
            acc.otherCard += r.otherCard;
            acc.onlineCardTotal += r.onlineCardTotal;
            acc.totalCard += r.totalCard;
            return acc;
          }, {
            oneStopCard:0, snappyCard:0, deliverooCard:0, justEatCard:0, otherCard:0, onlineCardTotal:0, totalCard:0
          });

          return (
            <div key={week} style={{ marginBottom:"20px" }}>
              <h3>{week}</h3>
              <table>
                <thead>
                  <tr>
                    <th>OneStop Card</th>
                    <th>Snappy Card</th>
                    <th>Deliveroo Card</th>
                    <th>Just Eat Card</th>
                    <th>Other Card</th>
                    <th>Online Card Total</th>
                    <th>Total Card Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.oneStopCard.toFixed(2)}</td>
                      <td>{r.snappyCard.toFixed(2)}</td>
                      <td>{r.deliverooCard.toFixed(2)}</td>
                      <td>{r.justEatCard.toFixed(2)}</td>
                      <td>{r.otherCard.toFixed(2)}</td>
                      <td>{r.onlineCardTotal.toFixed(2)}</td>
                      <td>{r.totalCard.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight:"bold", background:"#f0f0f0" }}>
                    <td>{weekTotals.oneStopCard.toFixed(2)}</td>
                    <td>{weekTotals.snappyCard.toFixed(2)}</td>
                    <td>{weekTotals.deliverooCard.toFixed(2)}</td>
                    <td>{weekTotals.justEatCard.toFixed(2)}</td>
                    <td>{weekTotals.otherCard.toFixed(2)}</td>
                    <td>{weekTotals.onlineCardTotal.toFixed(2)}</td>
                    <td>{weekTotals.totalCard.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <h3 style={{ marginTop: "10px" }}>
          Grand Total: £{grandTotals.totalCard.toFixed(2)}
        </h3>
      </div>
    );
  }
}
